/**
 * PaletteHarmonyGenerator
 * Generates a palette from a base color + harmony type + variation.
 * Used inline in the PaletteList creation flow.
 */

import { useState, useMemo } from "react";
import { Sparkles, X } from "lucide-react";
import { colorEngine, type HarmonyType } from "../../lib/colorEngine";

interface PaletteHarmonyGeneratorProps {
  /** Called when the user confirms creation. Passes the generated colors and an auto-derived name. */
  onCreatePalette: (colors: string[], autoName: string) => void;
  /** Called when the user dismisses the generator */
  onCancel: () => void;
  /** Optional initial base color (e.g. from context) */
  initialColor?: string;
}

type Variation = "vivid" | "muted" | "pastel" | "dark";

const HARMONY_TYPES: { type: HarmonyType; label: string; icon: string }[] = [
  { type: "complementary", label: "Complementary", icon: "◐" },
  { type: "analogous", label: "Analogous", icon: "◑" },
  { type: "triadic", label: "Triadic", icon: "△" },
  { type: "splitComplementary", label: "Split Comp.", icon: "⋔" },
  { type: "tetradic", label: "Tetradic", icon: "◇" },
];

const VARIATIONS: { key: Variation; label: string }[] = [
  { key: "vivid", label: "Vivid" },
  { key: "muted", label: "Muted" },
  { key: "pastel", label: "Pastel" },
  { key: "dark", label: "Dark" },
];

function applyVariation(colors: string[], variation: Variation): string[] {
  if (variation === "vivid") return colors;

  return colors.map((hex) => {
    const parsed = colorEngine.parseColor(hex);
    if (!parsed?.hsl) return hex;

    const { h, s, l } = parsed.hsl;

    switch (variation) {
      case "muted":
        return `hsl(${h}, ${Math.round(s * 0.5)}%, ${l}%)`;
      case "pastel":
        return `hsl(${h}, ${Math.round(s * 0.6)}%, ${Math.min(Math.round(l + 25), 92)}%)`;
      case "dark":
        return `hsl(${h}, ${Math.round(s * 0.8)}%, ${Math.max(Math.round(l * 0.5), 8)}%)`;
      default:
        return hex;
    }
  });
}

/** Normalise an HSL/hex mix back to hex strings */
function normalizeToHex(colors: string[]): string[] {
  return colors.map((c) => {
    const parsed = colorEngine.parseColor(c);
    return parsed?.hex ?? c;
  });
}

export function PaletteHarmonyGenerator({
  onCreatePalette,
  onCancel,
  initialColor,
}: PaletteHarmonyGeneratorProps) {
  const [baseColor, setBaseColor] = useState(initialColor || "#3b82f6");
  const [harmonyType, setHarmonyType] = useState<HarmonyType>("analogous");
  const [variation, setVariation] = useState<Variation>("vivid");

  const previewColors = useMemo(() => {
    const set = colorEngine.generateHarmonySet(baseColor, harmonyType);
    if (!set || set.length === 0) return [baseColor];
    const varied = applyVariation(set, variation);
    return normalizeToHex(varied);
  }, [baseColor, harmonyType, variation]);

  const autoName = useMemo(() => {
    const typeLabel =
      HARMONY_TYPES.find((h) => h.type === harmonyType)?.label ?? harmonyType;
    const varLabel = variation !== "vivid" ? ` (${variation})` : "";
    return `${typeLabel}${varLabel} · ${baseColor}`;
  }, [harmonyType, variation, baseColor]);

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-blue-500" />
          Generate from Harmony
        </h4>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          title="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Base color picker */}
      <div className="flex items-center gap-2">
        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
          Base
        </label>
        <input
          type="color"
          value={baseColor}
          onChange={(e) => setBaseColor(e.target.value)}
          className="h-8 w-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer bg-transparent"
        />
        <input
          type="text"
          value={baseColor}
          onChange={(e) => setBaseColor(e.target.value)}
          className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-mono"
          placeholder="#000000"
        />
      </div>

      {/* Harmony type */}
      <div className="flex flex-wrap gap-1">
        {HARMONY_TYPES.map(({ type, label, icon }) => (
          <button
            key={type}
            onClick={() => setHarmonyType(type)}
            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
              harmonyType === type
                ? "bg-blue-600 text-white"
                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            <span className="mr-1">{icon}</span>
            {label}
          </button>
        ))}
      </div>

      {/* Variation */}
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">
          Tone:
        </span>
        {VARIATIONS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setVariation(key)}
            className={`px-2 py-0.5 rounded text-xs transition-colors ${
              variation === key
                ? "bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900"
                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Preview strip */}
      <div className="flex rounded-lg overflow-hidden h-10 border border-gray-200 dark:border-gray-600">
        {previewColors.map((color, i) => (
          <div
            key={i}
            className="flex-1 transition-colors duration-200"
            style={{ backgroundColor: color }}
            title={color}
          />
        ))}
      </div>

      {/* Create button */}
      <button
        onClick={() => onCreatePalette(previewColors, autoName)}
        className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
      >
        <Sparkles className="w-3.5 h-3.5" />
        Create Palette
      </button>
    </div>
  );
}
